import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fixImageUrl } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiPlus, FiTrash2, FiUser, FiArrowLeft, FiEdit3, FiX, FiSave, FiImage, FiHeart, FiStar } = FiIcons;

const PeopleManager = () => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);

  const initialForm = {
    name: '',
    relation: '',
    side_of_family: 'maternal',
    bio: '',
    photo_url: '',
    favorite_things: ''
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchPeople();
  }, []);

  const fetchPeople = async () => {
    setLoading(true);
    const { data } = await supabase.from('people_20240520').select('*').order('name');
    setPeople(data || []);
    setLoading(false);
  };

  const handleEdit = async (person) => {
    setFormData(person);
    setEditingId(person.id);
    const { data: media } = await supabase.from('person_media_20240520').select('*').eq('person_id', person.id);
    setMediaItems(media || []);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let personId = editingId;
      if (editingId) {
        await supabase.from('people_20240520').update(formData).eq('id', editingId);
      } else {
        const { data } = await supabase.from('people_20240520').insert([formData]).select().single();
        personId = data.id;
      }

      if (editingId) await supabase.from('person_media_20240520').delete().eq('person_id', editingId);
      const validMedia = mediaItems.filter(m => m.url.trim() !== '');
      if (validMedia.length > 0) {
        await supabase.from('person_media_20240520').insert(validMedia.map((m, i) => ({ person_id: personId, url: m.url, sort_order: i })));
      }

      toast.success('Saved successfully');
      setShowForm(false);
      setEditingId(null);
      setFormData(initialForm);
      setMediaItems([]);
      fetchPeople();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-10">
        <div>
          <button onClick={() => window.history.back()} className="text-sm font-bold text-[#8c7851] flex items-center mb-2 hover:underline">
            <SafeIcon icon={FiArrowLeft} className="mr-1" /> Dashboard
          </button>
          <h1 className="text-4xl font-serif font-bold text-[#2c2c2c]">Family Tree</h1>
        </div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData(initialForm); setMediaItems([]); }} className="px-6 py-3 bg-[#8c7851] text-white rounded-xl font-bold flex items-center gap-2">
          <SafeIcon icon={showForm ? FiX : FiPlus} />
          <span>{showForm ? 'Cancel' : 'Add Person'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-12 bg-white p-8 rounded-[2rem] border-2 border-[#8c7851]/20 shadow-xl space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Full Name</label>
              <input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200" placeholder="e.g. Grandma Rose" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Title / Relation</label>
              <input required value={formData.relation} onChange={e => setFormData({ ...formData, relation: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200" placeholder="e.g. Great Grandmother" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Family Branch</label>
              <select value={formData.side_of_family} onChange={e => setFormData({ ...formData, side_of_family: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50">
                <option value="maternal">Maternal (Mom's Side)</option>
                <option value="paternal">Paternal (Dad's Side)</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Main Photo URL</label>
              <input value={formData.photo_url} onChange={e => setFormData({ ...formData, photo_url: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200" placeholder="Link to profile photo" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
              <SafeIcon icon={FiStar} className="text-amber-500" /> Favorite Things
            </label>
            <input value={formData.favorite_things} onChange={e => setFormData({ ...formData, favorite_things: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-stone-200" placeholder="e.g. Gardening, Baking, Traveling to Italy" />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Biography / Life Story</label>
            <textarea value={formData.bio} onChange={e => setFormData({ ...formData, bio: e.target.value })} className="w-full px-4 py-4 rounded-xl border border-stone-200 h-32" placeholder="Tell their story..." />
          </div>

          <div className="bg-stone-50 p-6 rounded-2xl border border-stone-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-sm flex items-center gap-2">
                <SafeIcon icon={FiImage} className="text-[#8c7851]" /> Additional Life Photos
              </h3>
              <button type="button" onClick={() => setMediaItems([...mediaItems, { url: '' }])} className="text-xs font-bold text-[#8c7851] bg-white px-3 py-1.5 rounded-lg border border-stone-200 shadow-sm">Add URL</button>
            </div>
            <div className="space-y-3">
              {mediaItems.map((item, idx) => (
                <div key={idx} className="flex gap-2">
                  <input value={item.url} onChange={e => { const n = [...mediaItems]; n[idx].url = e.target.value; setMediaItems(n); }} className="flex-1 px-3 py-2 text-sm rounded-lg border border-stone-200" placeholder="Image URL" />
                  <button type="button" onClick={() => setMediaItems(mediaItems.filter((_, i) => i !== idx))} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" className="w-full bg-[#2c2c2c] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-black transition-all">
            <SafeIcon icon={FiSave} />
            <span>{editingId ? 'Update Family Member' : 'Save New Family Member'}</span>
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {people.map(p => (
          <div key={p.id} className="bg-white p-4 rounded-2xl border border-stone-100 flex items-center justify-between group hover:border-[#8c7851]/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-stone-50">
                <img src={fixImageUrl(p.photo_url)} className="w-full h-full object-cover" alt="" />
              </div>
              <div>
                <h3 className="font-bold text-[#2c2c2c]">{p.name}</h3>
                <p className="text-xs text-[#8c7851] font-medium uppercase tracking-widest">{p.relation}</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => handleEdit(p)} className="p-2.5 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors"><SafeIcon icon={FiEdit3} /></button>
              <button onClick={async () => { if(confirm('Delete?')) { await supabase.from('people_20240520').delete().eq('id', p.id); fetchPeople(); } }} className="p-2.5 text-red-400 hover:bg-red-50 rounded-xl transition-colors"><SafeIcon icon={FiTrash2} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PeopleManager;